import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import reactCSS from 'reactcss';
import { Card, Grid, FormControl, FormControlLabel, IconButton, ListItem, Paper, Typography, useTheme } from '@mui/material';
import TrashIcon from '../../../icons/TrashIcon';
import apiClient from '../../../../services/api';
import store from '../../../../store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { createConfirmAction } from '../../../../store/actions/ConfirmDialog';
import { LoadingScreen } from '../../../partials/LoadingScreen';
import StyledTextField from '../../../styled/TextField';
import StyledButton from '../../../styled/Button';

const BackgroundsSearch = ({t, searchBackground, filterBackground}) => {
    return (
        <Fragment>
            <label htmlFor="search" style={{display: 'block', marginTop: '10px'}}>{t('Search background')}</label>
            <Grid container alignContent="center" mb={2}>
                <Grid item xs={12}>
                    <StyledTextField
                        value={searchBackground}
                        onChange={filterBackground}
                        id="search"
                        size="small"
                        fullWidth
                        variant="outlined"
                    />
                </Grid>
            </Grid>
        </Fragment>
    )
}

const BackgroundListContainer = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const BackgroundList = ({filteredBackgrounds, t}) => {
        return (
            filteredBackgrounds && filteredBackgrounds.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 140} itemSize={96} itemCount={filteredBackgrounds.length} overscanCount={8} style={{ overflow: 'auto', textOverflow: 'ellipsis'}}>
                            {props.renderBackgroundRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (filteredBackgrounds.length === 0 ? 'block' : 'none'), background: theme.palette.storeDrawer.tabs.label.background}}>
                    {t('No backgrounds found!')}
                </Typography>
            )
        )
    }

    const BackgroundsDelete = ({handleSelectAll, allSelected, handleDelete}) => {
        return (
            <Grid container alignItems='center' alignContent='center' style={{marginBottom: '5px'}}>
                <Grid
                    item
                    style={{
                        display: 'flex',
                        justifyContent: 'start',
                        backgroundColor: theme.palette.background.tab,
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        borderRadius: '8px'
                    }}
                >
                    <input
                        type='checkbox'
                        checked={allSelected}
                        style={{marginRight: '5px', marginLeft: '15px'}}
                        onChange={handleSelectAll}
                    />
                    <IconButton 
                        onClick={() => {
                            store.dispatch(createConfirmAction(handleDelete, t('Are you sure you want to delete all selected backgrounds?'), { type: 'selected' }))
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
            </Grid>
        )
    }

    return (
        <Paper sx={{padding: '15px', height: '102%'}}>
            <Typography variant='h1'>{t('Backgrounds')}</Typography>
            <BackgroundsSearch
                t={t}
                searchBackground={props.searchBackgrounds}
                filterBackground={props.filterBackgrounds}
            />
            <BackgroundsDelete
                handleSelectAll={props.handleSelectAll}
                allSelected={props.allSelected}
                handleDelete={props.handleDelete}
            />
            <BackgroundList
                filteredBackgrounds={props.filteredBackgrounds}
                t={t}
            />
        </Paper>
    )
}

const BackgroundForm = (props) => {
    const FileInput = ({onChangeCallback, name}) => {
        return (<input type="file" id="image" name={name} onChange={onChangeCallback} style={{marginLeft: '5px'}}/>)
    }

    return (
        <Paper sx={{padding: '15px'}}>
            <Typography variant='h2' style={{marginBottom: '10px'}}>{props.t('Edit background')}</Typography>
            <StyledTextField
                size="small"
                margin="dense"
                id="name"
                label={props.t("Name")}
                type="text"
                fullWidth
                variant="outlined"
                value={props.name}
                onChange={props.handleChangeName}
                error={props.errors.name}
                helperText={props.errors.name}
            />
            <div style={{marginBottom: '10px'}}>
                <FormControl component='fieldset'>
                    <FormControlLabel
                        style={{marginLeft: 0}}
                        control={<FileInput name={props.image} onChangeCallback={props.handleFileUpload} />}
                        label={props.t('Upload file')+': '}
                        labelPlacement='start'/>
                </FormControl>
            </div>
            {props.image ? 
                <div>
                    <img src={props.image} style={{ width: '100%', maxWidth: '250px' }} alt="image" />
                </div> : null
            }
            <StyledButton
                variant="contained"
                style={{marginTop: '15px'}}
                onClick={props.handleSave}
            >
                {props.selectedBackground ? props.t('Update') : props.t('Add')}
            </StyledButton>
            {props.image ? 
                <StyledButton
                    background="secondary"
                    variant="outlined"
                    style={{marginLeft: '10px', marginTop: '15px'}}
                    onClick={props.clearAllSelection}
                >
                { props.t('Cancel')}
                </StyledButton>
            : null}
        </Paper>
    )
}

function Backgrounds() {
    const {t} = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const [selectedBackground, setSelectedBackground] = useState(null);
    const [allSelected, setAllSelected] = useState(false);
    const [backgrounds, setBackgrounds] = useState([]);
    const [filteredBackgrounds, setFilteredBackgrounds] = useState([]);
    const [searchBackgrounds, setSearchBackgrounds] = useState('');
    const [errors, setErrors] = React.useState([]);

    const [name, setName] = useState('');
    const [image, setImage] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);    
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'backgrounds'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeName = (event) => { setName(event.target.value) }

    const styles = reactCSS({
        'default': {
            swatch: {
                padding: '5px',
                background: '#fff',
                borderRadius: '1px',
                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                display: 'inline-block',
                cursor: 'pointer',
            },
            popover: {
                position: 'absolute',
                zIndex: '2',
            },
            cover: {
                position: 'fixed',
                top: '0px',
                right: '0px',
                bottom: '0px',
                left: '0px',
            },
        },
    })

    useEffect(() => {
        if(selectedBackground) {
            setName(selectedBackground.name);
            setImage(selectedBackground.image);
        }
    }, [selectedBackground])

    useEffect((navigate) => {
        apiClient.get('/api/backgrounds').then(response => {
            response.data.backgrounds.map(function(item) { item.selected = false; return item});

            setBackgrounds(response.data.backgrounds);
            setFilteredBackgrounds(response.data.backgrounds);
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t("Backgrounds can't be loaded"), "error"));
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps
    
    const getData = () => {
        const formData = new FormData();

        formData.append('id', (selectedBackground ? selectedBackground.id : 0));
        formData.append('name', name);
        formData.append('image', uploadedFile ? uploadedFile : null);
        
        return formData;
    }

    const handleFileUpload = (event) => {
        const [file] = event.target.files;

        if(file)
        {
            setImage(URL.createObjectURL(file));
            setUploadedFile(file);
        }
    }

    const handleDelete = (params) => {
        let ids;

        if(params.type === 'selected') {
            ids = filteredBackgrounds?.filter(x => x.selected).map(x => x.id);
        } else {
            ids = [params?.item?.id];
        }

        apiClient.post('/api/backgrounds/delete', { ids }).then(response => {
            if(response.data.success) {
                setBackgrounds(response.data.backgrounds);
                setFilteredBackgrounds(response.data.backgrounds);
                
                store.dispatch(toggleMessageOpen(t('Background deleted successfully'), "success"));
            }
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")));
    }
    
    const handleSave = () => {
        const headers = {'Content-Type': 'multipart/form-data'};
        const data = getData();

        apiClient.post('/api/backgrounds/save', data, { headers }).then(response => {
            if(response.data.success) {
                setBackgrounds(response.data.backgrounds);
                setFilteredBackgrounds(response.data.backgrounds);
                setUploadedFile(null);
                clearSelected();

                store.dispatch(toggleMessageOpen(t('Background saved successfully!'), 'success'));
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'));
            }
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred!'), 'error'));
        });
    }

    const renderBackgroundRow = (rowProps) => {
        const { index, style } = rowProps;
      
        return (
            <ListItem
                style={style}
                sx={{marginBottom: '10px'}}
                key={`background`+index}
                component="div"
                disablePadding
                onClick={(event) => handleBackgroundClicked(event, index)}
            >
                <Card 
                    sx={{
                        width: '100%',
                        backgroundColor: theme.palette.background.tab,
                        border: localStorage.getItem('currentTheme') !== 'Dark'
                            ? '1px solid ' + theme.palette.border.main
                            : 'none'
                    }}
                >
                    <Grid container alignItems='center' justifyItems='center' style={{height: '86px'}} spacing={1}>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'start'}}>
                            <input 
                                type="checkbox"
                                checked={filteredBackgrounds[index]['selected']}
                                onChange={(event) => handelSelectedChange(event, index)}
                                onClick={(event) => {event.stopPropagation()}}
                                style={{marginLeft: '15px'}}
                            />
                        </Grid>
                        <Grid item xs={3} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={filteredBackgrounds[index]['image']} style={{width: '60px'}} alt="image" />
                        </Grid>
                        <Grid item xs={7}>
                            <div>{filteredBackgrounds[index]['name']}</div>
                        </Grid>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'end'}}>
                            <IconButton
                                onClick={(event) => {
                                    console.log('BACKGROUND: ', filteredBackgrounds[index]);

                                    event.stopPropagation();
                                    store.dispatch(createConfirmAction(handleDelete, t('Are you sure you want to delete this background?'), { type: 'single', item: filteredBackgrounds[index] }))
                                }}
                            >
                                <TrashIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Card>
            </ListItem>
        );
    }
    
    const filterBackgrounds = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = backgrounds.filter(
                background => {
                    const value = background.name;

                    return value.toLowerCase().includes(keyword.toLowerCase());
                }
            );
  
            setFilteredBackgrounds(results);
        } else {
            setFilteredBackgrounds(backgrounds);
        }
  
        setSearchBackgrounds(keyword);
    }

    const clearSelected = () => {
        setName('');
        setImage('');

        setSelectedBackground(null);
        setErrors([]);
    }

    const handleBackgroundClicked = (event, index) => {
        const updateList = [...filteredBackgrounds];

        updateList.map(function(item, i) {
            if(index !== i) {
                item.selected = false;
            } else {
                item.selected = true;
            }
            
            return item;
        });

        setFilteredBackgrounds(updateList);
        setSelectedBackground(filteredBackgrounds[index]);
        setErrors([]);
    }

    const handelSelectedChange = (event, index) => {
        const updateList = [...filteredBackgrounds];
        updateList[index]['selected'] = event.target.checked;

        let nrOfSelected = 0;

        updateList.forEach(function(item) {
            if(item.selected) {
                nrOfSelected++
            }
        })

        if(nrOfSelected === 0 || nrOfSelected > 1) {
            clearSelected()
        }

        setFilteredBackgrounds(updateList);
    }

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        const updateList = [...filteredBackgrounds];

        updateList.map(function(item, i) { item.selected = checked; return item });

        setFilteredBackgrounds(updateList);
        setAllSelected(checked);
    }

    const handleUnselectAll = () => {
        const updateList = [...filteredBackgrounds];

        updateList.map(function(item, i) { item.selected = false; return item });

        setFilteredBackgrounds(updateList);
        setAllSelected(false);
    }

    const clearAllSelection = () => {
        clearSelected();
        handleUnselectAll();
    }

    const renderData = () => {
        return (
            <Grid container spacing={3} sx={{height: '100%'}}>
                <Grid item xs={6}>
                    <BackgroundListContainer
                        filteredBackgrounds={filteredBackgrounds}
                        searchBackgrounds={searchBackgrounds}
                        allSelected={allSelected}
                        filterBackgrounds={filterBackgrounds}
                        renderBackgroundRow={renderBackgroundRow}
                        clearSelected={clearSelected}
                        handleSelectAll={handleSelectAll}
                        handleDelete={handleDelete}
                    />
                </Grid>
                <Grid item xs={4}>
                    <BackgroundForm
                        name={name}
                        image={image}
                        selectedBackground={selectedBackground}
                        errors={errors}
                        handleFileUpload={handleFileUpload}
                        setName={setName}
                        setImage={setImage}
                        handleChangeName={handleChangeName}
                        handleSave={handleSave}
                        clearAllSelection={clearAllSelection}
                        styles={styles}
                        t={t}
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    );
}

export default Backgrounds;